import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AppPage from './AppPage';
import EntryScreen from './EntryScreen';
import BusinessBillingView from './BusinessBillingView';
import SignView from './SignView';
import CardView from './CardView';
import DoneView from './DoneView';
import PendingView from './PendingView';
import InvalidLink from './InvalidLink';
import { getSignupState, getSignupStatus } from '../../api/signup';

// The server owns all step transitions — this component only renders the
// step it reports and refreshes state when the poll says progress was made.
const POLL_STEPS = ['sign', 'card'];

function SignupWizard() {
  const { token } = useParams();
  const queryClient = useQueryClient();
  // The purple entry card ("confirm this is you") shows once per visit,
  // before the merged Business & Billing screen — a client-side gate only;
  // the server's identity/billing steps both render behind it.
  const [started, setStarted] = useState(false);

  const {
    data: state,
    error,
    isLoading,
    refetch: refetchState,
  } = useQuery({
    queryKey: ['signup-state', token],
    queryFn: () => getSignupState(token),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: data =>
      data?.step === 'pending_approval' ? 15 * 1000 : false,
  });

  const step = state?.step;

  useQuery({
    queryKey: ['signup-status', token],
    queryFn: () => getSignupStatus(token),
    enabled: POLL_STEPS.includes(step),
    refetchInterval: 3 * 1000,
    // signing is embedded, but keep polling while backgrounded — the
    // merchant may leave the tab mid-signature and return
    refetchIntervalInBackground: true,
    onSuccess: status => {
      if (
        (step === 'sign' && status?.signed) ||
        (step === 'card' && status?.carded)
      ) {
        refetchState();
      }
    },
  });

  function onStateChange(newState) {
    queryClient.setQueryData(['signup-state', token], newState);
  }

  if (isLoading) {
    return (
      <AppPage loading>
        <h1 className="signup-title">Loading your signup…</h1>
      </AppPage>
    );
  }

  if (error || !state) {
    return <InvalidLink error={error} />;
  }

  switch (step) {
    case 'pending_approval':
      return <PendingView state={state} />;
    case 'identity':
    case 'billing':
      // One flow (owner, Aug 6): both server steps render as one screen.
      return started ? (
        <BusinessBillingView
          token={token}
          state={state}
          onStateChange={onStateChange}
          onBack={() => setStarted(false)}
        />
      ) : (
        <EntryScreen
          token={token}
          state={state}
          onStateChange={onStateChange}
          onStart={() => setStarted(true)}
        />
      );
    case 'sign':
      return <SignView token={token} state={state} />;
    case 'card':
      return <CardView token={token} state={state} />;
    case 'done':
      return <DoneView state={state} />;
    default:
      return <InvalidLink />;
  }
}

export default SignupWizard;
